import { Location, locationName, locationUserPermission } from '@cobliteam/common-types';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import type FeatureFlags from '@cobliteam/hooks/src/useFeatureFlags/featureFlags';
import useIsOnboardingFleet from '@cobliteam/hooks/src/useIsOnboardingFleet/useIsOnboardingFleet';
import { lazy } from '@cobliteam/lazy-with-retries';
import { type Permission, usePermissions } from '@cobliteam/permissions';
import AccessDenied from '@cobliteam/ui-business/components/AccessDenied';
import type { ComponentType, ReactNode } from 'react';
import { Redirect, Route, type RouteComponentProps, useLocation } from 'react-router-dom';
import PageChunkLoadingPage from '../../pages/LoadingPage/PageChunkLoadingPage';
import MaintenancePage from '../../pages/MaintenancePage';

import routes, { type IRoute } from '../../routes';

// Lazy loading of Providers - mounted only on routes / pages that require their
// functionalities. Suspensions intentionally bubble to AppContentLoadingBoundary,
// which keeps the boot progress continuous and uses a local loader after boot.
const BlueprintProvider = lazy(() => import('../../providers/BlueprintProvider/BlueprintProvider').then((m) => ({ default: m.BlueprintProvider })));
const GoogleMapsProvider = lazy(() => import('../../providers/GoogleMapsProvider/GoogleMapsProvider').then((m) => ({ default: m.GoogleMapsProvider })));

interface PrivateRouteProps {
  route: IRoute;
}

const getSafeUnauthorizedFallback = (search: string): string | null => {
  const fallback = new URLSearchParams(search).get('unauthorizedFallback');
  if (!fallback) return null;
  if (!fallback.startsWith('/') || fallback.startsWith('//') || fallback.includes('..')) return null;
  return fallback;
};

const recursivePaths = (routesDef: IRoute[]): IRoute[] => {
  const paths: IRoute[] = [];
  for (const r of routesDef) {
    paths.push(r);
    if (r.routes) paths.push(...recursivePaths(r.routes));
  }
  return paths;
};

const allRoutes = recursivePaths(routes as IRoute[]);

function findRoute(pathname: string): IRoute | undefined {
  return allRoutes.find((route) => {
    const pathRegex = new RegExp(`^${route.path?.replace(/:[a-zA-Z]+/g, '[a-zA-Z0-9-]+')}$`);
    return pathRegex.test(pathname);
  });
}

function isRouteUnderMaintenance(maintenance: unknown, routePath?: string): boolean {
  return Array.isArray(maintenance) && maintenance.some((route: { url?: string }) => route.url === routePath);
}

function getOnboardingGuard(selectedRoute: IRoute | undefined, currentRoutePath: IRoute['path'], isLoading: boolean, isOnboardingFleet: boolean): ReactNode | undefined {
  if (!selectedRoute?.requiresOnboardingFleet) return undefined;
  if (isLoading) return currentRoutePath === selectedRoute.path ? <PageChunkLoadingPage /> : null;
  if (!isOnboardingFleet) return <Redirect to={Location.HOME} />;
  return undefined;
}

const PUBLIC_ROUTES: readonly Location[] = [Location.LOGIN, Location.TOKEN_VALIDATION, Location.PASSWORD_RECOVERY, Location.PASSWORD_RESET, Location.MFA_CHALLENGE];

type RenderRouteContentOptions = {
  route: IRoute;
  selectedRoute: IRoute;
  routeProps: RouteComponentProps;
  search: string;
  isAllowed: boolean;
  hasGroupsRestriction: boolean;
  isMaintenance: boolean;
};

function renderRouteContent({ route, selectedRoute, routeProps, search, isAllowed, hasGroupsRestriction, isMaintenance }: RenderRouteContentOptions): ReactNode {
  if (!isAllowed) {
    const fallback = getSafeUnauthorizedFallback(search);
    if (fallback) return <Redirect to={fallback} />;
    return <AccessDenied pageDescription={selectedRoute.accessDeniedDescription} />;
  }

  if (hasGroupsRestriction) return <AccessDenied />;

  const routePath = selectedRoute.path;
  if (isMaintenance) return <MaintenancePage title={selectedRoute.title || locationName[routePath] || ''} path={routePath} />;

  const Component = route.component as ComponentType<RouteComponentProps & { routes?: IRoute[] }>;
  let content = <Component {...routeProps} routes={route.routes} />;

  if (route.needsGoogleMaps) content = <GoogleMapsProvider>{content}</GoogleMapsProvider>;
  if (route.needsBlueprint) content = <BlueprintProvider>{content}</BlueprintProvider>;

  return content;
}

const PrivateRoute = ({ route }: PrivateRouteProps) => {
  const { isAccessAllowed } = usePermissions();
  const { hasGroupsRestriction, session } = useUser();
  const location = useLocation();
  const featureToggleFlags = useFeatureFlags();
  const { isOnboardingFleet, isLoading: isOnboardingLoading } = useIsOnboardingFleet();

  const selectedRoute = findRoute(location.pathname);
  const isMaintenance = isRouteUnderMaintenance(featureToggleFlags.maintenance, selectedRoute?.path);
  const isInactiveFleet = session?.fleet.active === false;

  // redirect to home / if path is empty
  if (location.pathname === Location.EMPTY) return <Redirect to={Location.HOME} />;

  // Frota inativa: deep links fora de boletos voltam para boletos (o LayoutSwitcher cobre a
  // navegação normal; este guard cobre o acesso direto por URL)
  if (isInactiveFleet && selectedRoute && selectedRoute.path !== Location.BUSINESS_MANAGER_BOLETOS) return <Redirect to={Location.BUSINESS_MANAGER_BOLETOS} />;

  // A rota de boletos não pode 404ar para uma frota inativa, mesmo com o feature flag
  // businessManager desligado — é a única página que ela consegue acessar
  const isInactiveFleetBoletosRoute = isInactiveFleet && selectedRoute?.path === Location.BUSINESS_MANAGER_BOLETOS;
  if (!isInactiveFleetBoletosRoute && selectedRoute?.featureFlag && featureToggleFlags[selectedRoute.featureFlag as keyof FeatureFlags] === false) return <Redirect to={Location.NOT_FOUND} />;

  // Router renders every PrivateRoute without a Switch, so only the matching
  // onboarding route may render its loading state.
  const onboardingGuard = getOnboardingGuard(selectedRoute, route.path, isOnboardingLoading, isOnboardingFleet);
  if (onboardingGuard !== undefined) return onboardingGuard;

  // after login and router is public - hotfix
  if (!selectedRoute && PUBLIC_ROUTES.includes(location.pathname as Location)) return <Redirect to={Location.HOME} />;

  // router not found
  if (!selectedRoute) return <Redirect to={Location.NOT_FOUND} />;

  const routePath = selectedRoute.path;
  // Uma Location pode exigir uma permissão única ou uma lista com semântica OR (ex.: BASIC_VALUE,
  // AE-2446) — isAccessAllowed já aceita ambas.
  const requiredPermission = locationUserPermission[routePath] as Permission | Permission[] | undefined;
  const isAllowed = !requiredPermission || isAccessAllowed(requiredPermission);
  const userHasGroupsRestriction = Boolean(route.hasGroupsRestriction && hasGroupsRestriction);

  // change title
  if (location.pathname === routePath) {
    document.title = `Cobli - ${selectedRoute.title || locationName[routePath] || ''}`;
  }

  return (
    <Route
      path={route.path}
      render={(routeProps: RouteComponentProps) =>
        renderRouteContent({
          route,
          selectedRoute,
          routeProps,
          search: location.search,
          isAllowed,
          hasGroupsRestriction: userHasGroupsRestriction,
          isMaintenance,
        })
      }
      exact={route.exact}
    />
  );
};

export default PrivateRoute;
